import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import axios from 'axios';
import {
  DollarSign, Plus, Search, Filter, Check, X, ChevronDown,
  FileText, Fuel, Wrench, Shield, Milestone, Package, Users,
  Building, Phone, Printer, MoreHorizontal, Calendar, Truck,
  TrendingUp, Clock, CheckCircle, XCircle, Edit, Trash2,
  ChevronLeft, ChevronRight, Download, Receipt, Eye, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { AccountingExportsTab } from '../components/accounting/AccountingExportsTab';
import { ChartOfAccountsTab } from '../components/accounting/ChartOfAccountsTab';
import { ExportTemplatesTab } from '../components/accounting/ExportTemplatesTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  fuel: Fuel, wrench: Wrench, 'shield-check': Shield, milestone: Milestone,
  shield: Shield, 'file-badge': FileText, package: Package, container: Package,
  users: Users, building: Building, phone: Phone, printer: Printer,
  ellipsis: MoreHorizontal, receipt: Receipt,
};

const PAYMENT_METHODS = [
  { value: 'carte_credit', label: 'Carte de credit' },
  { value: 'carte_debit', label: 'Carte de debit' },
  { value: 'comptant', label: 'Comptant' },
  { value: 'virement', label: 'Virement' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'compte_client', label: 'Compte client' },
];

const PROVINCES = [
  'QC', 'ON', 'NB', 'NS', 'PE', 'NL', 'MB', 'SK', 'AB', 'BC', 'YT', 'NT', 'NU',
  'NY', 'VT', 'NH', 'ME', 'MA', 'CT', 'PA', 'OH', 'MI', 'IN', 'IL', 'Other',
];

const STATUS_STYLES = {
  submitted: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30', label: 'En attente' },
  approved: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'Approuvee' },
  rejected: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30', label: 'Refusee' },
};

// ==================== SUMMARY TAB ====================

const SummaryTab = ({ token }) => {
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/expenses/summary?period=${period}`, { headers });
      setSummary(res.data);
    } catch { /* silent */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, period]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  if (loading) return <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" /></div>;
  if (!summary) return null;

  const maxCat = Math.max(...Object.values(summary.by_category || {}), 1);

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex gap-2" data-testid="period-selector">
        {[['month', 'Ce mois'], ['quarter', 'Ce trimestre'], ['year', 'Cette annee']].map(([val, label]) => (
          <button key={val} onClick={() => setPeriod(val)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${period === val ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
            data-testid={`period-${val}`}
          >{label}</button>
        ))}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="kpi-total">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center"><DollarSign size={20} className="text-amber-500" /></div>
            <span className="text-slate-400 text-sm">Total depenses</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.total_all.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</p>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="kpi-approved">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center"><CheckCircle size={20} className="text-emerald-500" /></div>
            <span className="text-slate-400 text-sm">Approuvees</span>
          </div>
          <p className="text-2xl font-bold text-emerald-400">{summary.total_approved.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</p>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="kpi-pending">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center"><Clock size={20} className="text-amber-500" /></div>
            <span className="text-slate-400 text-sm">En attente</span>
          </div>
          <p className="text-2xl font-bold text-amber-400">{summary.total_pending.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</p>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="kpi-pending-count">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center"><FileText size={20} className="text-blue-500" /></div>
            <span className="text-slate-400 text-sm">A approuver</span>
          </div>
          <p className="text-2xl font-bold text-blue-400">{summary.pending_count}</p>
        </div>
      </div>

      {/* Category breakdown */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6" data-testid="category-breakdown">
        <h3 className="text-white font-semibold mb-4">Repartition par categorie</h3>
        {Object.keys(summary.by_category || {}).length === 0 ? (
          <p className="text-slate-500 text-sm">Aucune depense pour cette periode</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(summary.by_category).map(([cat, amount]) => {
              const pct = (amount / maxCat) * 100;
              return (
                <div key={cat} className="flex items-center gap-3">
                  <span className="text-slate-300 text-sm w-48 truncate">{cat}</span>
                  <div className="flex-1 bg-slate-800 rounded-full h-3 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-white text-sm font-medium w-28 text-right">{amount.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== EXPENSE FORM MODAL ====================

const ExpenseFormModal = ({ expense, categories, vehicles, onSave, onClose, token }) => {
  const [form, setForm] = useState({
    category: expense?.category || 'Carburant',
    amount: expense?.amount || '',
    tax_amount: expense?.tax_amount || '',
    date: expense?.date || new Date().toISOString().split('T')[0],
    description: expense?.description || '',
    vendor: expense?.vendor || '',
    reference_number: expense?.reference_number || '',
    vehicle_id: expense?.vehicle_id || '',
    payment_method: expense?.payment_method || '',
    notes: expense?.notes || '',
    fuel_liters: expense?.fuel_liters || '',
    fuel_price_per_liter: expense?.fuel_price_per_liter || '',
    fuel_province: expense?.fuel_province || 'QC',
    odometer_km: expense?.odometer_km || '',
  });
  const [saving, setSaving] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);

  const hasReceipt = expense?.receipt_url || expense?.invoice_id;

  useEffect(() => {
    if (hasReceipt && expense?.id) {
      const loadReceipt = async () => {
        try {
          const res = await axios.get(`${API_URL}/api/expenses/${expense.id}/receipt`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
          });
          setReceiptUrl(URL.createObjectURL(res.data));
        } catch { /* no receipt available */ }
      };
      loadReceipt();
    }
    return () => { if (receiptUrl) URL.revokeObjectURL(receiptUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expense?.id]);

  const isFuel = form.category === 'Carburant';
  const selectedVehicle = vehicles.find(v => v.id === form.vehicle_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error('Montant requis'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount) || 0,
        tax_amount: parseFloat(form.tax_amount) || 0,
        fuel_liters: isFuel && form.fuel_liters ? parseFloat(form.fuel_liters) : null,
        fuel_price_per_liter: isFuel && form.fuel_price_per_liter ? parseFloat(form.fuel_price_per_liter) : null,
        fuel_province: isFuel ? form.fuel_province : null,
        odometer_km: form.odometer_km ? parseFloat(form.odometer_km) : null,
        vehicle_info: selectedVehicle ? `${selectedVehicle.unit_number || ''} ${selectedVehicle.brand || ''} ${selectedVehicle.model || ''}`.trim() : '',
      };
      const headers = { Authorization: `Bearer ${token}` };
      if (expense) {
        await axios.put(`${API_URL}/api/expenses/${expense.id}`, payload, { headers });
        toast.success('Depense modifiee');
      } else {
        await axios.post(`${API_URL}/api/expenses/`, payload, { headers });
        toast.success('Depense ajoutee');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="expense-form-modal">
        <div className="p-5 border-b border-slate-800 flex justify-between items-center">
          <h3 className="text-white font-bold text-lg">{expense ? 'Modifier' : 'Nouvelle depense'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Receipt Preview */}
          {hasReceipt && (
            <div className="space-y-2">
              <button type="button" onClick={() => setShowReceipt(!showReceipt)}
                className="w-full flex items-center justify-between bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg px-4 py-2.5 text-sm transition-colors"
                data-testid="toggle-receipt-btn">
                <span className="flex items-center gap-2 text-amber-400 font-medium">
                  <Eye size={16} /> Voir le document scanne
                </span>
                <ChevronDown size={16} className={`text-slate-400 transition-transform ${showReceipt ? 'rotate-180' : ''}`} />
              </button>
              {showReceipt && receiptUrl && (
                <div className="bg-slate-800 rounded-xl p-2 border border-slate-700">
                  <img src={receiptUrl} alt="Recu" className="w-full max-h-80 object-contain rounded-lg" data-testid="receipt-image" />
                </div>
              )}
              {showReceipt && !receiptUrl && (
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 text-center">
                  <FileText size={24} className="text-slate-500 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">Chargement du recu...</p>
                </div>
              )}
            </div>
          )}
          {/* Category */}
          <div>
            <label className="text-slate-400 text-sm mb-1 block">Categorie</label>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="expense-category-select">
              {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>

          {/* Amount + Tax */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Montant (avant taxes)</label>
              <input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="0.00" data-testid="expense-amount-input" />
            </div>
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Taxes (TPS+TVQ)</label>
              <input type="number" step="0.01" value={form.tax_amount} onChange={e => setForm({ ...form, tax_amount: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="0.00" data-testid="expense-tax-input" />
            </div>
          </div>

          {/* Date + Vendor */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Date</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="expense-date-input" />
            </div>
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Fournisseur</label>
              <input value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="Ex: Petro-Canada" data-testid="expense-vendor-input" />
            </div>
          </div>

          {/* Vehicle + Payment */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Vehicule</label>
              <select value={form.vehicle_id} onChange={e => setForm({ ...form, vehicle_id: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="expense-vehicle-select">
                <option value="">-- Aucun --</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.unit_number || v.plate_number} - {v.brand} {v.model}</option>)}
              </select>
            </div>
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Mode de paiement</label>
              <select value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="expense-payment-select">
                <option value="">-- Choisir --</option>
                {PAYMENT_METHODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>

          {/* Fuel-specific */}
          {isFuel && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 space-y-3" data-testid="fuel-fields">
              <p className="text-amber-400 text-xs font-semibold flex items-center gap-2"><Fuel size={14} /> Donnees carburant (IFTA)</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Litres</label>
                  <input type="number" step="0.01" value={form.fuel_liters} onChange={e => setForm({ ...form, fuel_liters: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-sm" data-testid="fuel-liters-input" />
                </div>
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Prix/litre</label>
                  <input type="number" step="0.001" value={form.fuel_price_per_liter} onChange={e => setForm({ ...form, fuel_price_per_liter: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-sm" data-testid="fuel-price-input" />
                </div>
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Province/Etat</label>
                  <select value={form.fuel_province} onChange={e => setForm({ ...form, fuel_province: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-sm" data-testid="fuel-province-select">
                    {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-slate-400 text-xs mb-1 block">Odometre (km)</label>
                <input type="number" step="1" value={form.odometer_km} onChange={e => setForm({ ...form, odometer_km: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-sm" data-testid="fuel-odometer-input" />
              </div>
            </div>
          )}

          {/* Reference + Description */}
          <div>
            <label className="text-slate-400 text-sm mb-1 block">No. de reference / facture</label>
            <input value={form.reference_number} onChange={e => setForm({ ...form, reference_number: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="expense-ref-input" />
          </div>
          <div>
            <label className="text-slate-400 text-sm mb-1 block">Description</label>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="expense-desc-input" />
          </div>
          <div>
            <label className="text-slate-400 text-sm mb-1 block">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm resize-none" data-testid="expense-notes-input" />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 bg-slate-800 text-slate-300 py-2.5 rounded-lg text-sm font-medium hover:bg-slate-700" data-testid="expense-cancel-btn">Annuler</button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-amber-500 text-black py-2.5 rounded-lg text-sm font-bold hover:bg-amber-400 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="expense-save-btn">
              {saving ? <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : <Check size={16} />}
              {expense ? 'Enregistrer' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== EXPENSES LIST TAB ====================

const ExpensesListTab = ({ token, onPendingCountChange }) => {
  const [expenses, setExpenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/expenses/?page=${page}&limit=30`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (filterCategory) url += `&category=${filterCategory}`;
      const res = await axios.get(url, { headers });
      setExpenses(res.data.expenses || []);
      setTotal(res.data.total || 0);
      const pending = (res.data.expenses || []).filter(e => e.status === 'submitted').length;
      onPendingCountChange?.(pending);
    } catch { /* silent */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page, filterStatus, filterCategory]);

  const fetchMeta = useCallback(async () => {
    try {
      const [catRes, vehRes] = await Promise.all([
        axios.get(`${API_URL}/api/expenses/categories`, { headers }),
        axios.get(`${API_URL}/api/saas/fleet/vehicles`, { headers }).catch(() => ({ data: [] })),
      ]);
      setCategories(catRes.data || []);
      setVehicles(vehRes.data || []);
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const handleApprove = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/expenses/${id}/approve`, {}, { headers });
      toast.success('Depense approuvee');
      fetchExpenses();
    } catch { toast.error('Erreur'); }
  };

  const handleReject = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/expenses/${id}/reject`, {}, { headers });
      toast.success('Depense refusee');
      fetchExpenses();
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette depense?')) return;
    try {
      await axios.delete(`${API_URL}/api/expenses/${id}`, { headers });
      toast.success('Depense supprimee');
      fetchExpenses();
    } catch { toast.error('Erreur'); }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    try {
      await axios.patch(`${API_URL}/api/expenses/bulk-approve`, { ids: selectedIds }, { headers });
      toast.success(`${selectedIds.length} depense(s) approuvee(s)`);
      setSelectedIds([]);
      fetchExpenses();
    } catch { toast.error('Erreur'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const totalPages = Math.ceil(total / 30);

  return (
    <div className="space-y-4">
      {/* Filters + Actions */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2 items-center">
          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="filter-status">
            <option value="">Tous les statuts</option>
            <option value="submitted">En attente</option>
            <option value="approved">Approuvees</option>
            <option value="rejected">Refusees</option>
          </select>
          <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setPage(1); }}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="filter-category">
            <option value="">Toutes categories</option>
            {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          {selectedIds.length > 0 && (
            <button onClick={handleBulkApprove}
              className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2" data-testid="bulk-approve-btn">
              <CheckCircle size={16} /> Approuver ({selectedIds.length})
            </button>
          )}
          <button onClick={() => { setEditingExpense(null); setShowForm(true); }}
            className="bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2" data-testid="add-expense-btn">
            <Plus size={16} /> Nouvelle depense
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-10 h-10 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" /></div>
      ) : expenses.length === 0 ? (
        <div className="text-center py-16 bg-slate-900 border border-slate-800 rounded-xl">
          <Receipt size={48} className="text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Aucune depense trouvee</p>
          <button onClick={() => setShowForm(true)} className="mt-3 text-amber-500 text-sm font-medium hover:underline" data-testid="empty-add-btn">Ajouter une depense</button>
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="expenses-table">
              <thead>
                <tr className="border-b border-slate-800 text-slate-400 text-left">
                  <th className="p-3 w-10"><input type="checkbox" className="accent-amber-500"
                    checked={selectedIds.length === expenses.filter(e => e.status === 'submitted').length && selectedIds.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) { setSelectedIds(expenses.filter(ex => ex.status === 'submitted').map(ex => ex.id)); }
                      else { setSelectedIds([]); }
                    }} data-testid="select-all-checkbox" /></th>
                  <th className="p-3">Date</th>
                  <th className="p-3">Categorie</th>
                  <th className="p-3">Description</th>
                  <th className="p-3">Fournisseur</th>
                  <th className="p-3">Vehicule</th>
                  <th className="p-3 text-right">Montant</th>
                  <th className="p-3">Statut</th>
                  <th className="p-3">Soumis par</th>
                  <th className="p-3 w-28">Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map(exp => {
                  const st = STATUS_STYLES[exp.status] || STATUS_STYLES.submitted;
                  return (
                    <tr key={exp.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors" data-testid={`expense-row-${exp.id}`}>
                      <td className="p-3">
                        {exp.status === 'submitted' && (
                          <input type="checkbox" className="accent-amber-500" checked={selectedIds.includes(exp.id)} onChange={() => toggleSelect(exp.id)} />
                        )}
                      </td>
                      <td className="p-3 text-white whitespace-nowrap">{exp.date}</td>
                      <td className="p-3 text-slate-300">{exp.category}</td>
                      <td className="p-3 text-slate-300 max-w-[200px] truncate">{exp.description || '-'}</td>
                      <td className="p-3 text-slate-400">{exp.vendor || '-'}</td>
                      <td className="p-3 text-slate-400 text-xs">{exp.vehicle_info || '-'}</td>
                      <td className="p-3 text-right font-medium text-white whitespace-nowrap">{exp.total_amount?.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${st.bg} ${st.text} border ${st.border}`}>{st.label}</span>
                      </td>
                      <td className="p-3 text-slate-400 text-xs">{exp.submitted_by_name}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          {exp.status === 'submitted' && (
                            <>
                              <button onClick={() => handleApprove(exp.id)} title="Approuver" className="p-1.5 text-emerald-400 hover:bg-emerald-500/10 rounded" data-testid={`approve-${exp.id}`}><Check size={14} /></button>
                              <button onClick={() => handleReject(exp.id)} title="Refuser" className="p-1.5 text-red-400 hover:bg-red-500/10 rounded" data-testid={`reject-${exp.id}`}><X size={14} /></button>
                            </>
                          )}
                          <button onClick={() => { setEditingExpense(exp); setShowForm(true); }} title="Modifier" className="p-1.5 text-slate-400 hover:bg-slate-700 rounded" data-testid={`edit-${exp.id}`}><Edit size={14} /></button>
                          {exp.receipt_url && (
                            <button onClick={() => { setEditingExpense(exp); setShowForm(true); }} title="Voir le recu" className="p-1.5 text-amber-400 hover:bg-amber-500/10 rounded" data-testid={`receipt-${exp.id}`}><ImageIcon size={14} /></button>
                          )}
                          <button onClick={() => handleDelete(exp.id)} title="Supprimer" className="p-1.5 text-red-400 hover:bg-red-500/10 rounded" data-testid={`delete-${exp.id}`}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center p-3 border-t border-slate-800">
              <span className="text-slate-400 text-sm">{total} depense(s)</span>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 text-slate-400 hover:text-white disabled:opacity-30"><ChevronLeft size={16} /></button>
                <span className="text-slate-300 text-sm px-3 py-2">{page} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 text-slate-400 hover:text-white disabled:opacity-30"><ChevronRight size={16} /></button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <ExpenseFormModal
          expense={editingExpense}
          categories={categories}
          vehicles={vehicles}
          token={token}
          onClose={() => { setShowForm(false); setEditingExpense(null); }}
          onSave={() => { setShowForm(false); setEditingExpense(null); fetchExpenses(); }}
        />
      )}
    </div>
  );
};

// ==================== CATEGORIES TAB ====================

const CategoriesTab = ({ token }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCat, setNewCat] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/expenses/categories`, { headers });
      setCategories(res.data || []);
    } catch { /* silent */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await axios.post(`${API_URL}/api/expenses/categories`, { name: newCat.trim() }, { headers });
      setNewCat('');
      fetchCategories();
      toast.success('Categorie ajoutee');
    } catch { toast.error('Erreur'); }
  };

  const deleteCategory = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/expenses/categories/${id}`, { headers });
      fetchCategories();
      toast.success('Categorie supprimee');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-2xl space-y-6">
      {/* Add */}
      <div className="flex gap-3" data-testid="add-category-form">
        <input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="Nouvelle categorie..."
          onKeyDown={e => e.key === 'Enter' && addCategory()}
          className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="new-category-input" />
        <button onClick={addCategory} className="bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 rounded-lg text-sm font-bold" data-testid="add-category-btn">
          <Plus size={16} />
        </button>
      </div>

      {/* List */}
      <div className="space-y-2" data-testid="categories-list">
        {categories.map(cat => {
          const Icon = ICON_MAP[cat.icon] || MoreHorizontal;
          return (
            <div key={cat.id} className="flex items-center justify-between bg-slate-900 border border-slate-800 rounded-lg px-4 py-3">
              <div className="flex items-center gap-3">
                <Icon size={18} className="text-amber-500" />
                <span className="text-white text-sm">{cat.name}</span>
                <span className="text-slate-500 text-xs">({cat.type === 'fixed' ? 'Fixe' : 'Variable'})</span>
              </div>
              {!cat.is_default && (
                <button onClick={() => deleteCategory(cat.id)} className="text-red-400 hover:bg-red-500/10 p-1.5 rounded" data-testid={`delete-cat-${cat.id}`}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==================== MAIN PAGE ====================

const ExpensesPage = () => {
  const { token } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('list');
  const [pendingCount, setPendingCount] = useState(0);

  const tabs = [
    { key: 'list', label: 'Depenses', icon: FileText },
    { key: 'summary', label: 'Sommaire', icon: TrendingUp },
    { key: 'categories', label: 'Categories', icon: Filter },
    { key: 'exports', label: 'Exports', icon: Download },
    { key: 'accounts', label: 'Plan de comptes', icon: Building },
    { key: 'templates', label: "Modeles d'export", icon: FileText },
  ];

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Comptabilite</h1>
          <p className="text-slate-400 text-sm mt-1">Depenses, exports et plan de comptes : ProPilote alimente n'importe quel logiciel comptable</p>
        </div>
        {pendingCount > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-2 flex items-center gap-2" data-testid="pending-badge">
            <Clock size={16} className="text-amber-500" />
            <span className="text-amber-400 text-sm font-medium">{pendingCount} en attente d'approbation</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 bg-slate-900 border border-slate-800 rounded-lg p-1 w-fit" data-testid="expense-tabs">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-amber-500 text-black' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
              data-testid={`tab-${tab.key}`}>
              <Icon size={16} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {activeTab === 'list' && <ExpensesListTab token={token} onPendingCountChange={setPendingCount} />}
      {activeTab === 'summary' && <SummaryTab token={token} />}
      {activeTab === 'categories' && <CategoriesTab token={token} />}
      {activeTab === 'exports' && <AccountingExportsTab token={token} />}
      {activeTab === 'accounts' && <ChartOfAccountsTab token={token} />}
      {activeTab === 'templates' && <ExportTemplatesTab token={token} />}
    </div>
  );
};

export default ExpensesPage;
